<template>
  <div>
    <b-table
      class="mb-0 alarms-widget__data-table unique-table-class"
      :items="items"
      :fields="visibleFields"
      primary-key="id"
      :busy="busy"
      show-empty
      small
      :empty-text="idTable === 'user_alarms' ? '' : $t('grid.widgets.alarms.table.no_data')"
      responsive
      @row-dblclicked="onRowClick"
      :tbody-tr-class="rowClass"
      :tbody-tr-attr="rowAttrs"
      @sort-changed="onSortChanged"
    >
      <template #head()="data">
        <span> {{ $t(data.label) }}</span>
      </template>

      <!-- Level dot -->
      <template #head(level)="data">
        <span
          v-if="!sortingDisabled"
          class="level-sort-indicator"
          :class="{ 'is-active': sortState.by === 'level' }"
          v-b-tooltip.hover
          :title="sortTitleFor('level')"
        >
          {{ sortIconFor('level') }}
        </span>
      </template>

      <!-- Acronym header con indicador -->
      <template #head(alarm_acronym)="data">
        <span>{{ $t(data.label) }}</span>
        <span
          v-if="!sortingDisabled"
          class="level-sort-indicator ml-25"
          :class="{ 'is-active': sortState.by === 'alarm_acronym' }"
          v-b-tooltip.hover
          :title="sortTitleFor('alarm_acronym')"
        >
          {{ sortIconFor('alarm_acronym') }}
        </span>
      </template>

      <!-- Date header con indicador -->
      <template #head(created_format_1)="data">
        <span>{{ $t(data.label) }}</span>
        <span
          v-if="!sortingDisabled"
          class="level-sort-indicator ml-25"
          :class="{ 'is-active': sortState.by === 'created_format_1' }"
          v-b-tooltip.hover
          :title="sortTitleFor('created_format_1')"
        >
          {{ sortIconFor('created_format_1') }}
        </span>
      </template>

      <template #cell(level)="row">
        <span
          :style="{
            display: 'inline-block',
            marginLeft: '4px',
            width: '4px',
            height: '17px',
            backgroundColor: `${getAlarmLevelColor(row.item) || getTypeAlarmColor(row.item) || getAiDecisionColor(row.item)}`,
          }"
        ></span>
      </template>
      <!-- End Level dot -->

      <!-- Acronym -->
      <template #cell(alarm_acronym)="row">
        <span
          class="badge-wrapper"
          @mouseenter="showAlarmPopover(row.item)"
          @mouseleave="hideAlarmPopover(row.item)"
        >
          <span
            class="px-2 badge cursor-pointer badge-title"
            :style="{
              backgroundColor: getTypeAlarmColor(row.item) || '#000',
              color: getTypeAlarmTextColor(row.item) || '#fff',
            }"
            :id="`alarm-${row.item.id}`"
          >
            {{ row.item.alarm_acronym }}
          </span>
          <span class="badge-notification-slot">
            <span
              v-if="Number(row.item.recurrence_count) > 1"
              class="badge-notification"
            >
              {{ row.item.recurrence_count }}
            </span>
          </span>
          <span
            v-if="getDevicePanel(row.item).status"
            class="badge-panel-device-status"
            :style="{ backgroundColor: getDevicePanel(row.item).color }"
          ></span>
        </span>

      </template>
      <!-- End Acronym -->

      <!-- Icon -->
      <template #cell(icon)="row">
        <!-- Sensors popovers -->
        <div class="d-flex align-items-center justify-content-center">
          <!-- Smart Detection Icon -->
          <span
            v-if="getTypeAlarmIcon(row.item) && !hasDedicatedTypeIcon(row.item)"
            :id="`SD-${row.item.id}-${row.item._popoverUuid}`"
            @mouseover="configSD(row.item)"
          >
            <custom-icon
              :name="getTypeAlarmIcon(row.item)"
              size="18"
              :color="getTypeAlarmColorIcon(row.item) || getTypeAlarmColor(row.item) || getAiDecisionColor(row.item)"
              title=""
            />
          </span>
          <!-- <feather-icon
            :color="getAiDecisionColor(row.item)"
            v-else-if="row.item.alarm_acronym === 'SD'"
            :id="`SD1-${row.item.id}-${row.item._popoverUuid}`"
            icon="UserIcon"
            size="18"
            @mouseover="configSD(row.item)"
          /> -->
          <b-popover
            v-if="row.item.alarm_acronym === 'SD'"
            :target="`SD-${row.item.id}-${row.item._popoverUuid}`"
            placement="right"
            triggers="hover"
            boundary="window"
          >
            <img
              style="width: 350px"
              :src="imgUrl"
              @click="openImageModal(imgUrl)"
              v-b-modal.image_modal
            />
          </b-popover>

          <!-- SIP CALL Indicator -->
          <feather-icon
            :id="`sip-call-button-${row.item.id}-${row.item._popoverUuid}`"
            v-if="row.item.sip_call.length"
            icon="PhoneCallIcon"
            :stroke="
              row.item.sip_call[0].state === 'waiting'
                ? 'red'
                : row.item.sip_call[0].state === 'answered' ||
                  row.item.sip_call[0].state === 'transfer'
                ? 'orange'
                : 'gray'
            "
            size="18"
            :class="[
              {
                'on-call':
                  row.item.sip_call[0].state === 'waiting' ||
                  row.item.sip_call[0].state === 'answered',
              },
            ]"
          />
          <b-popover
            :target="`sip-call-button-${row.item.id}-${row.item._popoverUuid}`"
            v-if="row.item.sip_call.length"
            placement="right"
            triggers="hover"
            boundary="window"
          >
            {{
              row.item.sip_call[0].destAddr
                ? row.item.sip_call[0].unit_user === undefined
                  ? `Llamada contestada por:${row.item.sip_call[0].remotePartyNumber}`
                  : `Llamada contestada por:${row.item.sip_call[0].unit_user}`
                : row.item.sip_call[0].state
            }}
          </b-popover>

          <!-- PBX CALL Indicator -->
          <feather-icon
            :id="`pbx-call-button-${row.item.id}-${row.item._popoverUuid}`"
            v-if="row.item.pbx_call && row.item.pbx_call.length"
            icon="PhoneCallIcon"
            :stroke="
              row.item.pbx_call[0].status === 'WAITING'
                ? 'red'
                : row.item.pbx_call[0].status === 'ANSWERED' ||
                  row.item.pbx_call[0].status === 'TRANSFER'
                ? 'orange'
                : 'gray'
            "
            size="18"
            :class="[
              {
                'on-call':
                  row.item.pbx_call[0].status === 'WAITING' ||
                  row.item.pbx_call[0].status === 'ANSWERED',
              },
            ]"
          />
          <b-popover
            :target="`pbx-call-button-${row.item.id}-${row.item._popoverUuid}`"
            v-if="row.item.pbx_call && row.item.pbx_call.length"
            placement="right"
            triggers="hover"
            boundary="window"
          >
            {{
              row.item.pbx_call[0].status === "ANSWERED" ||
              row.item.pbx_call[0].status === "HANGUP"
                ? `Llamada contestada por:${row.item.pbx_call[0].device
                  .filter((device) => device.pivot.rol === "receiver")
                  .map((device) => device.extension)}`
                : row.item.pbx_call[0].status
            }}
          </b-popover>

          <!-- FR Eye Indicator -->
          <span
            v-if="
              getTypeAlarmIcon(row.item) &&
              row.item.alarm_acronym === 'FR' &&
              !row.item.detection_event_data
            "
            :id="`fr-button-${row.item.id}-${row.item._popoverUuid}`"
          >
            <custom-icon
              :name="getTypeAlarmIcon(row.item)"
              size="18"
              :color="getTypeAlarmColorIcon(row.item) || getTypeAlarmColor(row.item) || getAiDecisionColor(row.item)"
              title=""
            />
          </span>
          <feather-icon
            :color="getAiDecisionColor(row.item)"
            :id="`fr-button-${row.item.id}-${row.item._popoverUuid}`"
            v-else-if="row.item.alarm_acronym === 'FR' && !row.item.detection_event_data"
            icon="EyeIcon"
            size="18"
          />
          <b-popover
            :target="`fr-button-${row.item.id}-${row.item._popoverUuid}`"
            v-if="row.item.alarm_acronym === 'FR'"
            triggers="hover"
            boundary="window"
          >
            <carrousel-default
              :idAlarm="row.item.id"
              :detEvent="row.item.detection_event_data"
              :allAlarmData="row.item"
              alarmType="fr"
            />
          </b-popover>

          <!-- LPR Indicator -->
          <span
            v-if="
              getTypeAlarmIcon(row.item) &&
              row.item.alarm_acronym === 'LPR' &&
              row.item.lpr_data
            "
            :id="`lpr-button-${row.item.id}-${row.item._popoverUuid}`"
          >
            <custom-icon
              :name="getTypeAlarmIcon(row.item)"
              size="18"
              :color="getTypeAlarmColorIcon(row.item) || getTypeAlarmColor(row.item) || getAiDecisionColor(row.item)"
              title=""
            />
          </span>
          <feather-icon
            :color="getAiDecisionColor(row.item)"
            :id="`lpr-button-${row.item.id}-${row.item._popoverUuid}`"
            v-else-if="row.item.alarm_acronym === 'LPR' && row.item.lpr_data"
            icon="TruckIcon"
            size="18"
          />
          <b-popover
            :target="`lpr-button-${row.item.id}-${row.item._popoverUuid}`"
            v-if="row.item.alarm_acronym === 'LPR' && row.item.lpr_data"
            placement="right"
            triggers="hover"
            boundary="window"
          >
            <carrousel-default
              :idAlarm="row.item.id"
              :detEvent="row.item.lpr_data"
              :allAlarmData="row.item"
              alarmType="lpr"
            />
          </b-popover>

          <!-- Detection Event -->
          <div
            v-if="
              row.item.detection_event_data &&
              !row.item.detection_event_data.fake_event &&
              !excludedDetectionEventAlerts.includes(row.item.alarm_acronym) &&
              !row.item.analytic_record
            "
          >
            <span
              v-if="getTypeAlarmIcon(row.item)"
              :id="`det-event-button-${row.item.id}-${row.item._popoverUuid}`"
            >
              <custom-icon
                :name="getTypeAlarmIcon(row.item)"
                size="18"
                :color="getTypeAlarmColorIcon(row.item) || getTypeAlarmColor(row.item) || getAiDecisionColor(row.item)"
                title=""
              />
            </span>
            <feather-icon
              v-else
              :color="getAiDecisionColor(row.item)"
              :id="`det-event-button-${row.item.id}-${row.item._popoverUuid}`"
              icon="EyeIcon"
              size="18"
            />
            <b-popover
              v-if="row.item.detection_event_data.provider !== 'alarms'"
              :target="`det-event-button-${row.item.id}-${row.item._popoverUuid}`"
              placement="right"
              triggers="hover"
              boundary="window"
            >
              <carrousel-default
                :idAlarm="row.item.id"
                :detEvent="row.item.detection_event_data"
                :allAlarmData="row.item"
                alarmType="fr"
              />
            </b-popover>
          </div>

          <!-- Panel/Device events -->
          <div
            v-if="row.item.detection_event_data && row.item.detection_event_data.fake_event">
            <span
              v-if="getTypeAlarmIcon(row.item)"
              :id="`det-event-button-${row.item.id}-${row.item._popoverUuid}`"
            >
              <custom-icon
                :name="getTypeAlarmIcon(row.item)"
                size="18"
                :color="getTypeAlarmColorIcon(row.item) || getTypeAlarmColor(row.item) || getAiDecisionColor(row.item)"
                title=""
              />
            </span>
          </div>

          <!-- IntelixVision carrousel -->
          <span
            v-if="getTypeAlarmIcon(row.item) && !!row.item.analytic_record"
            :id="`ar-button-${row.item.id}-${row.item._popoverUuid}`"
          >
            <custom-icon
              :name="getTypeAlarmIcon(row.item)"
              size="18"
              :color="getTypeAlarmColorIcon(row.item) || getTypeAlarmColor(row.item) || getAiDecisionColor(row.item)"
              title=""
            />
          </span>
          <feather-icon
            :color="getAiDecisionColor(row.item)"
            :id="`ar-button-${row.item.id}-${row.item._popoverUuid}`"
            v-else-if="!!row.item.analytic_record"
            icon="FilmIcon"
            size="18"
          />
          <b-popover
            :target="`ar-button-${row.item.id}-${row.item._popoverUuid}`"
            v-if="!!row.item.analytic_record"
            placement="right"
            triggers="hover"
            boundary="window"
          >
            <carrousel-default
              :idAlarm="row.item.analytic_record.id_alarm"
              :detEvent="row.item"
              :allAlarmData="row.item"
              alarmType="ar"
            />
          </b-popover>

          <b-popover
            v-if="row.item.data"
            :target="getTargetToMessageAiPopover(row)"
            placement="right"
            triggers="hover"
            boundary="window"
            custom-class="custom-popover"
          >
            <div class="">
              <span class="ai-comment-title">Created by AI.</span>
              <div class="">{{ getAiMessage(row.item) }}</div>
            </div>
          </b-popover>
        </div>
      </template>
      <!-- End Icon -->

      <!-- Device Name -->
      <template #cell(detection_event_data.camera_data.camera.name)="row">
        <span
          v-b-tooltip.hover
          :style="getRowStyle(row.item)"
          :title="`${
            (row.item &&
              row.item.detection_event_data &&
              row.item.detection_event_data.camera_data &&
              row.item.detection_event_data.camera_data.camera &&
              row.item.detection_event_data.camera_data.camera.name) ||
            ''
          }`"
        >
          {{
            (row.item &&
              row.item.detection_event_data &&
              row.item.detection_event_data.camera_data &&
              row.item.detection_event_data.camera_data.camera &&
              row.item.detection_event_data.camera_data.camera.name) ||
            ""
          }}
        </span>
      </template>
      <!-- End Device Name -->

      <!-- Facility -->
      <template #cell(unit.region.name)="row">
        <span
          v-b-tooltip.hover
          :title="`${
            (row.item &&
              row.item.unit &&
              row.item.unit.region &&
              row.item.unit.region.name) ||
            ''
          }`"
        >
          {{
            (row.item &&
              row.item.unit &&
              row.item.unit.region &&
              row.item.unit.region.name) ||
            ""
          }}
        </span>
      </template>
      <!-- End Facility -->

      <!-- Date -->
      <template #cell(created_format_1)="row">
        <span
          v-b-tooltip.hover
          :title="`${(row.item && row.item.created_format_1) || ''}`"
        >
          {{ (row.item && row.item.created_format_1) || "" }}
        </span>
      </template>
      <!-- End Date -->

      <!-- Actions -->
      <template #cell(actions)="row">
        <div class="d-flex justify-content-end">
          <!-- Alarm dropdown options -->
          <b-dropdown
            text="Ver"
            ref="dropdown"
            class="m-0 p-0"
            size="sm"
            variant="link"
            no-caret
            boundary="viewport"
            menu-class="dropdown-menu-actions"
            @shown="fixDropdownPosition"
          >
            <template #button-content>
              <feather-icon
                class="icon-actions-alarms"
                icon="MoreVerticalIcon"
                size="18"
              />
            </template>
            <!-- Maker-checker flow -->
            <span v-if="isMakerCheckerFlow">
              <template v-if="canSendToReview">
                <b-dropdown-item @click="onAlarmClickAction(row, 'send_to_review')">
                  <span class="text-warning">
                    <feather-icon class="mr-50" icon="UserCheckIcon" size="13"/>
                    Enviar a revisión
                  </span>
                </b-dropdown-item>
              </template>
              <template v-if="canReturnToAttention">
                <b-dropdown-item @click="onAlarmClickAction(row, 'return_to_attention')">
                  <span class="text-warning">
                    <feather-icon class="mr-50" icon="CornerUpLeftIcon" size="13"/>
                    Regresar a atención
                  </span>
                </b-dropdown-item>
              </template>
            </span>
            <template
              v-if="canMarkAsAttended"
            >
              <b-dropdown-item @click="onAlarmClickAction(row, 'attended')">
                <span class="text-success">
                  <feather-icon class="mr-50" icon="CheckCircleIcon" size="13"/>
                  {{ $t("grid.widgets.alarms.menu.attended") }}
                </span>
              </b-dropdown-item>
            </template>
            <template
              v-if="
                (filters.waiting || ['waiting'].includes(filters.filter)) &&
                $can('update_attention', 'Alarm') &&
                !isMakerCheckerFlow
              "
            >
              <b-dropdown-item @click="onAlarmClickAction(row, 'confirmed')">
                <span class="text-success">
                  <feather-icon class="mr-50" icon="CheckCircleIcon" size="13"/>
                  {{ $t("grid.widgets.alarms.menu.confirm") }}
                </span>
              </b-dropdown-item>
            </template>
            <template
              v-if="
                (filters.attending || ['attending'].includes(filters.filter)) &&
                $can('update_attend', 'Alarm') &&
                !isMakerCheckerFlow
              "
            >
              <b-dropdown-item @click="onAlarmClickAction(row, 'attended')">
                <span class="text-success">
                  <feather-icon class="mr-50" icon="CheckCircleIcon" size="13"/>
                  {{ $t("grid.widgets.alarms.menu.attended") }}
                </span>
              </b-dropdown-item>
            </template>

            <b-dropdown-item
              v-if="$can('update_cancel', 'Alarm') && !['in_review'].includes(filters.filter)"
              @click="onAlarmClickAction(row, 'canceled')"
            >
              <span class="text-danger">
                <feather-icon icon="XCircleIcon" class="mr-50" size="13"/>
                {{ $t("grid.widgets.alarms.menu.cancel") }}
              </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-for="tpl in alarmFormTemplates"
              :key="'tpl-' + tpl.id"
              @click="onAlarmClickAction(row, 'form_template', tpl)"
            >
              <span class="text-info">
                <feather-icon icon="FileTextIcon" class="mr-50" size="13"/>
                {{ tpl.name }}
              </span>
            </b-dropdown-item>

            <template v-if="$can('send_to_seproban', 'Alarm')">
              <b-dropdown-item @click="onAlarmClickAction(row, 'send_to_seproban')">
                <span class="text-info">
                  <feather-icon icon="SendIcon" class="mr-50" size="13"/>
                  {{ $t("grid.widgets.alarms.menu.send_to_seproban") }}
                </span>
              </b-dropdown-item>
            </template>


            <!-- <b-dropdown-item @click="onAlarmClickConfig(row)">
              <span class="text-info">
                <feather-icon icon="GridIcon" class="mr-50" size="13" />
                {{ $t("grid.widgets.alarms.menu.display") }}
              </span>
            </b-dropdown-item> -->
          </b-dropdown>
        </div>
      </template>
      <!-- End Actions -->
    </b-table>

    <!-- Acronym pop-over -->
    <b-popover
      v-for="item in items"
      :key="`popover-${location}-${item._popoverUuid}`"
      :target="`alarm-row-${location}-${item._popoverUuid}`"
      :show.sync="item.showPopover"
      triggers="manual"
      :placement="popoverPlacement"
      boundary="viewport"
      container="body"
    >
      <div>
        <!--<div>
          <strong>{{ $t("grid.widgets.alarms.table.popover.alarm_id") }}:</strong>
          {{ item.id }}
        </div>-->
        <div>
          <strong>{{ $t("grid.widgets.alarms.table.popover.date") }}:</strong>
          {{
            item.created_format_1 || $t("grid.widgets.alarms.table.popover.not_available")
          }}
        </div>
        <div>
          <strong>{{ $t("grid.widgets.alarms.table.popover.alarm_type") }}:</strong>
          {{ item.alarm_type }}
        </div>
        <div>
          <strong>{{ $t("grid.widgets.alarms.table.popover.facility") }}:</strong>
          {{
            (item.unit && item.unit.description) ||
            $t("grid.widgets.alarms.table.popover.not_available")
          }}
        </div>
        <div v-if="item.unit.sirh">
          <strong>SIRH:</strong>
          {{ item.unit.sirh }}
        </div>
        <div v-if="item.unit.rems">
          <strong>REMS:</strong>
          {{ item.unit.rems }}
        </div>
        <div v-if="item.unit.id_seproban">
          <strong>ID SEPROBAN:</strong>
          {{ item.unit.id_seproban }}
        </div>
        <div v-if="item.unit.sector">
          <strong>Región:</strong>
          {{ item.unit.sector.name }}
        </div>
        <div>
          <strong>{{ $t("grid.widgets.alarms.table.popover.camera") }}:</strong>
          {{
            (item.detection_event_data &&
              item.detection_event_data.camera_data &&
              item.detection_event_data.camera_data.camera &&
              item.detection_event_data.camera_data.camera.name) ||
            $t("grid.widgets.alarms.table.popover.not_available")
          }}
          <span v-if="getDevicePanel(item).source_id">
            ({{ getDevicePanel(item).source_id }})
          </span>
        </div>
        <div v-if="getDevicePanel(item).status">
          <strong>Estado:</strong>
          {{ getDevicePanel(item).status }}
        </div>
        <div v-if="getDevicePanel(item).last_activity_ct">
          <strong>{{ $t("grid.widgets.alarms.table.popover.central_hour") }}:</strong>
          {{ getDevicePanel(item).last_activity_ct }}
          <br/>
          <strong>{{ $t("grid.widgets.alarms.table.popover.local_hour") }}:</strong>
          {{ getDevicePanel(item).last_activity_lt }}
        </div>
        <div
          v-if="item.automatic_restored_at !== null && item.automatic_restored_at !== undefined"
        >
          <strong>{{ $t("grid.widgets.alarms.table.restored_at") }}:</strong>
          {{ formatAutomaticRestoredAt(item.automatic_restored_at) }}
        </div>
        <div
          v-if="getTakenBy(item)"
          class="isUserAlarms"
        >
          <strong>{{ $t("grid.widgets.alarms.table.popover.taken_by") }}:</strong>
          {{ getTakenBy(item) }}
        </div>
      </div>
    </b-popover>
    <!-- End Acronym pop-over -->
  </div>
</template>

<script>
import store from "@/store";
import axios from "@axios";
import {EventBus} from "@/libs/event-bus";
import CarrouselDefault from "./CarrouselDefault.vue";
import dateParser from "@/libs/date-parser";
import moment from "moment-timezone";
import {v4 as uuidv4} from "uuid";

import {
  BTable,
  BTooltip,
  BDropdown,
  BDropdownItem,
  BLink,
  BPopover,
  VBPopover,
} from "bootstrap-vue";
import LayoutApi from "@/libs/LayoutApi";

const layoutApi = new LayoutApi(store);

export default {
  components: {
    BTable,
    BTooltip,
    BDropdown,
    BDropdownItem,
    BLink,
    BPopover,
    CarrouselDefault,
  },
  directives: {
    "b-popover": VBPopover,
  },
  props: {
    idTable: {
      required: true,
      type: Number | String,
    },
    items: Array,
    location: String,
    busy: Boolean,
    filters: Object,
    isUserAlarms: {
      type: Boolean,
      default: false,
    },
  },
  data() {
   const orderFlag =  window.__env.VUE_ALARM_COLUM_ORDER || 'base';

    const baseFields = [
        {
          key: "level",
          label: "",
          minSize: 10,
          tdClass: "alarm-level-column",
          sortable: true,
        },
        {
          key: "icon",
          label: "",
          minSize: 200,
          tdClass: "alarm-icon-column"
        },
        {
          key: "alarm_acronym",
          label: "grid.widgets.alarms.table.rows.1",
          minSize: 200,
          tdClass: "alarm-acronym-column",
          sortable: true,
        },
        {
          key: "detection_event_data.camera_data.camera.name",
          label: "grid.widgets.alarms.table.rows.2",
          minSize: 350,
        },
        {
          key: "unit.description",
          label: "grid.widgets.alarms.table.rows.3",
          minSize: 200,
        },
        {
          key: "unit.region.name",
          label: "grid.widgets.alarms.table.rows.4",
          minSize: 600,
        },
        {
          key: "created_format_1",
          label: "grid.widgets.alarms.table.rows.5",
          minSize: 550,
          formatter: (value, key, item) => {
            return dateParser.parseDateTimezone(value);
          },
          sortable: true,
        },
        {key: "actions", label: "", minSize: 200, tdClass: "td-overflow-visible"},
      ]

  const ORDERS = {
    base: [
      "icon",
      "alarm_acronym",
      "detection_event_data.camera_data.camera.name",
      "unit.description",
      "unit.region.name",
      "created_format_1",
      "actions",
    ],
    bank: [
      "icon",
      "detection_event_data.camera_data.camera.name",
       "alarm_acronym",
      "unit.description",
      "unit.region.name",

      "created_format_1",
      "actions",
      ],
    };

    const order = ORDERS[orderFlag] || ORDERS.base;


    return {
      fields: this.reorderFields(baseFields, order),
      totalMatches: [],
      loadedImages: [],
      excludedDetectionEventAlerts: ["LPR"],
      imgUrl: null,
      selected: null,
      alarmFormTemplates: [],
      sortState: { by: 'created_format_1', desc: true },
    };
  },
  watch: {
    items() {
      this.$props.items.forEach((item) => {
        if (!item._popoverUuid) {
          this.$set(item, "_popoverUuid", uuidv4());
        }
      });

      let items = this.$props.items;
      const filteredArray = items.filter(
        (obj) =>
          obj.hasOwnProperty("facial_recognition") && obj.facial_recognition !== null
      );

      filteredArray.forEach(({facial_recognition}) => {
        (async () => {
          const {data} = await axios.post(
            `${window.__env.VUE_APP_BASE_URL}/v1/luna/getAlarmDataFRFirstMatch`,
            {
              alarmId: facial_recognition.alarm_id,
            }
          );
          const snapshot = data.data;
          this.loadedImages["fr_" + facial_recognition.alarm_id] = snapshot;
        })();
      });

      this.$props.items.forEach(async (row) => {
        if (row.sip_call.length) {
          if (row.sip_call[0].destAddr) {
            let unit_user = row.sip_call[0].remotePartyNumber;
            store
              .dispatch("sip/getDevice", {
                device_id: row.sip_call[0].remotePartyNumber,
              })
              .then((response) => {
                if (response.device) {
                  if (response.device.units) {
                    if (response.device.units.length) {
                      unit_user += " - " + response.device.units[0].description;
                    }
                  }
                } else if (response.user) {
                  unit_user += " - " + response.user.email;
                }
                this.$set(row.sip_call[0], "unit_user", unit_user);
              })
              .catch((e) => {
                console.log({e});
              });
          }
        }
      });

      this.$nextTick(() => {
        this.emitRowMetrics();
      });
    },
  },
  computed: {
    sidebarMinWidthReached() {
      return store.getters["grid/sidebarMinWidthReached"];
    },
    sidebarWidth() {
      if (this.location === "sideBarRigth") {
        return store.state.grid.sidebarRightWidth;
      }

      if (this.location === "sideBar") {
        return store.state.grid.sidebarWidth;
      }

      return store.state.grid.sidebarWidth;
    },
    visibleFields() {
      return this.fields
        .filter((f) => f.minSize <= this.sidebarWidth)
        .map((f) =>
          this.sortingDisabled ? { ...f, sortable: false } : f
        );
    },
    popoverPlacement() {
      return this.location === "sideBarRigth" ? "left" : "right";
    },
    tabs() {
      return store.state.grid.tabs;
    },
    tabsQty() {
      return this.tabs ? Object.keys(this.tabs).length : 0;
    },
    selectedAlarms() {
      return store.getters["grid/getVisited"];
    },
    user() {
      return store.getters["user/getUser"];
    },
    isReviewAlarm() {
      return this.filters.filter === 'in_review';
    },
    isMaintenanceAlarm() {
      return this.filters.filter === 'in_maintenance';
    },
    isAttending() {
      return this.filters.filter === 'attending';
    },
    isAttendingAlarm() {
      return this.filters.filter === 'user_alarms';
    },
    isMakerCheckerFlow() {
      return this.$can('maker_checker_flow', 'Alarm');
    },
    isMaintenanceFlow() {
      return this.$can('maintenance_flow', 'Alarm');
    },
    canSendToReview() {
      if (!this.isMakerCheckerFlow) return false;

      if (this.$can('validate_alarms', 'Alarm')) {
        return this.isAttending || this.isAttendingAlarm;
      }

      return !this.$can('validate_alarms', 'Alarm') && (this.isAttending || this.isAttendingAlarm || this.isReviewAlarm);
    },
    canReturnToAttention() {
      if (!this.isMakerCheckerFlow) return false;

      return this.$can('validate_alarms', 'Alarm') && this.isReviewAlarm;
    },
    canMarkAsAttended() {
      if (!this.isMakerCheckerFlow) return false;

      return this.$can('update_attend', 'Alarm') && this.isReviewAlarm;
    },
    sortingDisabled() {
      return this.isAttendingAlarm;
    },
  },
  created() {
    this.fetchAlarmFormTemplates();
  },
  methods: {
    onSortChanged(ctx) {
      this.sortState = { by: ctx?.sortBy, desc: ctx?.sortDesc };

      const fieldsMap = {
        alarm_acronym: 'acronym',
        level: 'level',
        created_format_1: 'created',
      };

      const sortInfo = {
        sortBy: fieldsMap[ctx?.sortBy] || ctx?.sortBy,
        sortDesc: ctx?.sortDesc,
      };

      this.$emit('sort-changed', sortInfo)
    },
    sortIconFor(key) {
      if (this.sortState.by !== key) return '▼';
      return this.sortState.desc ? '▼' : '▲';
    },
    sortTitleFor(key) {
      if (this.sortState.by !== key) return 'Orden';
      return this.sortState.desc ? 'Descendente' : 'Ascendente';
    },
    reorderFields(fields, order) {
        return [...fields].sort(
          (a, b) => order.indexOf(a.key) - order.indexOf(b.key)
        );
    },
    getRowStyle(item) {
      const flag = window.__env.VUE_ALARM_COLUM_ORDER

      if (!flag) return {};

      // const badgeBase = {
      //   display: "inline-block",
      //   padding: "0.25em 0.4em",
      //   fontSize: "75%",
      //   fontWeight: "700",
      //   lineHeight: "1",
      //   textAlign: "center",
      //   whiteSpace: "nowrap",
      //   verticalAlign: "baseline",
      //   borderRadius: "0.25rem",
      // };

      const badgeBase = {
        // .badge
        display: "inline-block",
        fontSize: "85%",
        fontWeight: "600",
        lineHeight: "1",
        verticalAlign: "baseline",

        // [dir] .badge
        padding: "0.3rem 0.5rem",
        textAlign: "center",
        borderRadius: "0.358rem",

        // .badge-title truncate
        maxWidth: "100%",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",

        transition:
          "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, background 0s, border 0s",
      };

      switch (flag) {
        case "bank":
          return {
            ...badgeBase,
            backgroundColor: this.getTypeAlarmColor(item) || "#000",
            color: this.getTypeAlarmTextColor(item) || "#fff",
          };

        default:
          return {};
      }
    },
    formatAutomaticRestoredAt(value) {
      return value || "";
    },
    fetchAlarmFormTemplates() {
      axios
        .get('/v1/operational-security-forms/templates', {
          params: {
            display_in_alarm: true,
          },
        })
        .then((response) => {
          this.alarmFormTemplates = response.data?.data || [];
        })
        .catch(() => {
          this.alarmFormTemplates = [];
        });
    },
    fixDropdownPosition() {
      this.$nextTick(() => {
        const menu = this.$el.querySelector('.dropdown-menu-actions.show');
        if (!menu) return;
        const toggle = menu.closest('.dropdown').querySelector('.dropdown-toggle');
        if (!toggle) return;
        const rect = toggle.getBoundingClientRect();
        const menuHeight = menu.offsetHeight;
        let top = rect.bottom + 2;
        if (top + menuHeight > window.innerHeight) {
          top = rect.top - menuHeight - 2;
        }
        let left = rect.right - menu.offsetWidth;
        if (left < 0) left = rect.left;
        menu.style.top = top + 'px';
        menu.style.left = left + 'px';
        menu.style.transform = 'none';
      });
    },
    onAlarmClickConfig({item}) {
      const selectedAlarm = item;
      const cameras = selectedAlarm.cameras;

      store.dispatch("grid/showLayoutModal", {
        source: "alarm",
        data: {
          obj: selectedAlarm,
          cameras,
        },
      });

      this.$root.$emit("bv::show::modal", "layout_modal");
    },
    async onAlarmClickAction({item}, action, template) {
      await store.dispatch("grid/setSelectedAlarm", {action, alarm: item});
      if (action === "form_template" && template) {
        const [date, time] = item.created_format_1.split(' ');
        this.$router.push({
          name: "OperationalSecurityFormAnswers",
          query: {
            startForm: String(template.id),
            requiresPrefill: 'true',
            unitId: item.unit?.id,
            id_alarm: item.id,
            date,
            time,
          },
        }).catch(() => {
        });
        return;
      }
      if (action === "send_to_seproban") {
        const hasExistingSeprobanProcess = (
          store.state.send_to_seproban.items.alarms || []
        ).some(alarm => Number(alarm.id_alarm) === Number(item.id));

        if (hasExistingSeprobanProcess) {
          store.commit("send_to_seproban/setNotification", true);
          this.$root.$emit("seproban:focus-alarm", item.id);
          return;
        }

        this.$root.$emit("bv::show::modal", "seproban_images");
        this.$emit("fetch-data");
      } else if (action === "send_to_review" || action === 'return_to_attention') {
        this.$root.$emit("bv::show::modal", "alarm_action_modal");
      } else {
        this.$root.$emit("bv::show::modal", "alarm_action_modal");
        this.$emit("fetch-data");
      }
    },
    configSD(alarm) {
      console.log("configSD", alarm.smart_detection_id)
      this.imgUrl = `${alarm.host}/v1/events/image/${alarm.smart_detection_id}`;
      return this.imgUrl;
    },
    configLPR(lprData) {
      return {
        html: true,
        boundary: "window",
        content: () => {
          return `<img style="width: 350px;" src="${lprData.general_image_url}">`;
        },
      };
    },
    async loadImg(url) {
      return new Promise(async (resolve) => {
        const config = {url, method: "get", responseType: "blob"};
        const blob = await axios.request(config);
        const reader = new FileReader();
        reader.addEventListener("loadend", () => resolve(reader.result));
        reader.readAsDataURL(blob.data);
      });
    },
    async onRowClick(item) {
      // Move alarm to User Alarms/Alarms In attention
      if (this.isAttending) {
        await this.takeOrReleaseAlarm("active_from_attending", item);
      }

      // Move alarm to User Alarms/Alarms In attention
      if (this.isReviewAlarm) {
        await this.takeOrReleaseAlarm("active_from_review", item);
      }

      if (this.isMaintenanceAlarm) {
        await this.takeOrReleaseAlarm("active_from_maintenance", item);
      }

      if (!this.isAttending && !this.isReviewAlarm && !this.isMaintenanceAlarm) {
        // Release alarm and move to attending, attending or in review panel
        if (this.isUserAlarms) {
          await this.takeOrReleaseAlarm("release", item);
          this.closeAlarmTab(item.id);
          return;
        }

        // Take alarm
        await this.takeOrReleaseAlarm("take", item);
      }

      if (this.location === "sideBarRigth") {
        if (this.$can("show_alarm_info_in_right_sidebar", "Alarm")) {
          await store.dispatch("grid/setAlarmId", item.id);
          store.commit("psimConfig/UPDATE_TOOLBARS_COLLAPSED_RIGHT", 0);
          store.commit("psimConfig/UPDATE_TOOLBARS_COLLAPSED", 1);
          await store.dispatch("grid/addVisited", {id: item.id, type: "alarm"});
        }

        if (this.$can("show_alarm_info_in_right_sidebar_and_tab", "Alarm")) {
          await store.dispatch("grid/clearAlarmTabs");
          await store.dispatch("grid/setAlarmView", {alarm: item});
        }

        this.selected = item.id;

        return;
      }

      if (this.tabsQty < store.getters["app/maxTabs"]) {
        await store.dispatch("grid/setAlarmView", {alarm: item});
      }

      this.selected = item.id;
    },
    async takeOrReleaseAlarm(action, item, data = {}) {
      if (this.$can("user_alarms", "Workspace")) {
        const payload = {
          id_alarm: item.id,
          action,
          data,
        };
        await store.dispatch("taken_alarms/takeAlarm", payload);
      }
    },
    closeAlarmTab(alarmId) {
      const tabs = layoutApi.getTabs();

      for (const tab in tabs) {
        const t = tabs[tab];
        if (t.context && t.context.isAlarm && t.context.alarmId === alarmId) {
          layoutApi.removeTab(tab);
          layoutApi.selectLastTab();
        }
      }
    },
    configAR(url) {
      return {
        html: true,
        boundary: "window",
        content: () => {
          return `<img style="width: 350px;" src="${url}">`;
        },
      };
    },
    async getDeviceUnitUser(user) {
      let unit_user = user;
      store
        .dispatch("sip/getDevice", {
          device_id: user,
        })
        .then((response) => {
          if (response.device.units) {
            if (response.device.units.length) {
              unit_user += " - " + response.device.units[0].description;
            }
          } else if (response.user) {
            unit_user += " - " + response.user.email;
          }
          //row.item.sip_call[0].unit_user = unit_user;
          return unit_user;
        })
        .catch((e) => {
          console.log({e});
        });
    },
    async openImageModal(imageUrl) {
      await store.dispatch("image_modal/imageUrl", imageUrl);
    },
    isAlarmWithPendingChanges(alarm) {
      return alarm?.alarm_user?.status === 'PENDING_CHANGES' ?? false;
    },
    rowClass(item, type) {
      if (!item || type !== "row") return;

      if (this.user && this.isMakerCheckerFlow) {
        const takenAlarm = this.checkIfAlarmIsTaken(item);

        if (takenAlarm !== undefined) {
          if (takenAlarm.user.id === this.user.id) {
            return "taken-row-owner table-alarms-waiting";
          }
          return "taken-row table-alarms-waiting";
        }
      }

      if (!this.isMakerCheckerFlow) {
        if (this.selected === item.id) return "selected-row table-alarms-waiting";

        const visited =
          this.selectedAlarms &&
          this.selectedAlarms.find((v) => v.id === item.id && v.type === "alarm");
        if (visited) {
          return "visited-row table-alarms-waiting";
        }

        if (this.getTargetToMessageAiPopover({item})) {
          return "table-alarms-waiting";
        }
      }

      if (this.isAlarmWithPendingChanges(item)) {
        return 'with-pending-changes table-alarms-waiting'
      }
    },
    getTargetToMessageAiPopover(row) {
      if (!row.item) return;
      const isFacialRecognition = row.item.alarm_acronym === "FR";
      const isLPR = row.item.alarm_acronym === "LPR" && row.item.lpr_data;
      const isDetectionEvent =
        row.item.detection_event_data &&
        //['luna', 'ntechlab'].includes(row.item.detection_event_data.provider) &&
        !this.excludedDetectionEventAlerts.includes(row.item.alarm_acronym) &&
        !row.item.analytic_record;
      const isAnalyticRecord = !!row.item.analytic_record;
      const isSD = row.item.alarm_acronym === "SD";

      const uuid = row.item._popoverUuid;

      if (isFacialRecognition) {
        return `fr-button-${row.item.id}-${uuid}`;
      }
      if (isLPR) {
        return `lpr-button-${row.item.id}-${uuid}`;
      }
      if (isDetectionEvent) {
        return `det-event-button-${row.item.id}-${uuid}`;
      }
      if (isAnalyticRecord) {
        return `ar-button-${row.item.id}-${uuid}`;
      }
      if (isSD) {
        return `SD-${row.item.id}-${uuid}`;
      }
      return "";
    },
    getAiMessage(row) {
      const aiImageContext = row.data?.find(
        (aiElement) => aiElement.type === "ai_image_context"
      );
      return aiImageContext?.data.context;
    },
    getAiDecisionColor(row) {
      const aiImageDecision = row.data?.find(
        (aiElement) => aiElement.type === "ai_decision"
      );
      return aiImageDecision?.data.color;
    },
    getTypeAlarmData(item) {
      const allTypeAlarms = store.getters["type_alarm/getAllItems"];
      const typeId = item.id_alarm_type || item.type;
      return allTypeAlarms.find((type) => type.id === typeId);
    },
    getTypeAlarmIcon(item) {
      const typeAlarmData = this.getTypeAlarmData(item);
      return typeAlarmData?.icon || null;
    },
    hasDedicatedTypeIcon(item) {
      const detectionEvent = item.detection_event_data;
      const hasDetectionEventBlock =
        !!detectionEvent &&
        !detectionEvent.fake_event &&
        !this.excludedDetectionEventAlerts.includes(item.alarm_acronym) &&
        !item.analytic_record;
      const hasFakeEventPanel = !!detectionEvent && !!detectionEvent.fake_event;
      return (
        (item.alarm_acronym === "FR" && !detectionEvent) ||
        (item.alarm_acronym === "LPR" && !!item.lpr_data) ||
        hasDetectionEventBlock ||
        hasFakeEventPanel ||
        !!item.analytic_record
      );
    },
    getTypeAlarmColor(item) {
      const typeAlarmData = this.getTypeAlarmData(item);
      return typeAlarmData?.color || null;
    },
    getTypeAlarmColorIcon(item) {
      const typeAlarmData = this.getTypeAlarmData(item);
      return typeAlarmData?.color_icon || null;
    },
    getTypeAlarmTextColor(item) {
      const typeAlarmData = this.getTypeAlarmData(item);
      return typeAlarmData?.text_color || null;
    },
    getAlarmLevelData(item) {
      const allAlarmLevels = store.getters["alarm_level/getAllItems"];
      return allAlarmLevels.find(
        (aL) => aL.alarmTypes.find(aT => aT.type === item.type)
      );
    },
    getAlarmLevelColor(item) {
      const alarmLevelData = this.getAlarmLevelData(item);
      return alarmLevelData?.color || null;
    },
    checkIfAlarmIsTaken(row) {
      // if (this.isUserAlarms) {
      //   return undefined;
      // }
      const takenAlarms = store.getters["taken_alarms/getTakenAlarms"];
      return takenAlarms.find((t) => t.id_alarm === row.id);
    },
    getTakenBy(row) {
      const takenAlarms = store.getters["taken_alarms/getTakenAlarms"];
      const alarm = takenAlarms.find((t) => t.id_alarm === row.id);

      if (alarm) {
        return alarm.user.full_name;
      }

      return "";
    },
    rowAttrs(item, type) {
      if (!item || type !== "row") return {};

      if (!item._popoverUuid) {
        this.$set(item, "_popoverUuid", uuidv4());
      }

      return {
        id: `alarm-row-${this.location}-${item._popoverUuid}`,
        'data-first-row': this.items[0] === item ? 'true' : null,
      };
    },
    showAlarmPopover(item) {
      this.$set(item, "showPopover", true);
    },
    hideAlarmPopover(item) {
      this.$set(item, "showPopover", false);
    },
    getDevicePanel(item) {
      if (!item.panel_device) {
        return {};
      }

      const device = item.panel_device;

      return {
        name: device.name,
        source_id: device.source_id,
        status: device.status,
        device_string: device.device_string,
        color: device.color,
        sirh: device?.sirh,
        rems: device?.rems,
        last_activity_ct: device.last_activity_ct_formatted, // Central Time
        last_activity_lt: device.last_activity_lt_formatted, // Local Time
      }
    },
    emitRowMetrics() {
      this.$nextTick(() => {
        const firstRow = this.$el.querySelector('[data-first-row="true"]');
        const headerRow = this.$el.querySelector('thead tr');

        if (!firstRow) return;

        const rowHeight = firstRow.getBoundingClientRect().height;
        const headerHeight = headerRow
          ? headerRow.getBoundingClientRect().height
          : 0;

        this.$emit("row-metrics", {
          rowHeight,
          headerHeight,
        });
      });
    },

  },
  mounted() {
    EventBus.on(`show-alarm-modal`, (params) => {
      this.$root.$emit("bv::show::modal", params);
    });

    EventBus.on(`show-alarm-config`, (params) => {
      this.onAlarmClickConfig({item: params});
    });

    EventBus.on(`show-alarm-row`, (params) => {
      this.onRowClick(params);
    });

    store.dispatch('type_alarm/getAllUnpaginated');
    store.dispatch('alarm_level/getAllUnpaginated');

    this.emitRowMetrics();
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/variables/variables";

.popover {
  max-width: none !important;

  .isUserAlarms {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
  }

  .popover-body {
    padding: 10px;
  }
}

[role="row"] {
  cursor: pointer;
}

.selected-row {
  background-color: $info;
  color: white;
}

.visited-row {
  background-color: var(--second-light) !important;
  color: white;
}

.taken-row {
  background-color: #ffc800 !important;
  color: white;
}

.with-pending-changes {
  background-color: #9a9a9a !important;
  color: white;
}

//.taken-row-owner {
//  background-color: #0fcd00 !important;
//  color: white;
//}

.alarms-widget {
  .table-responsive {
    min-height: 132px;
    overflow: visible !important;
  }

  .table.table-sm td {
    padding: 0 0.2rem !important;
    max-height: 50px;
    max-width: 140px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;

    .btn.dropdown-toggle {
      padding: 0.1rem 1rem;
    }
  }
}

.badge-title {
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.td-overflow-visible {
  overflow: visible !important;
}

.dropdown-menu-actions {
  position: fixed !important;
  z-index: 1050 !important;
  max-height: 300px;
  overflow-y: auto;
  max-width: 300px;
}

.selectable {
  cursor: pointer;
}

.on-call {
  -webkit-animation: flash linear 1s infinite;
  animation: flash linear 1s infinite;
}

@-webkit-keyframes flash {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.1;
  }
  100% {
    opacity: 1;
  }
}

@keyframes flash {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.1;
  }
  100% {
    opacity: 1;
  }
}

.dark-layout {
  .icon-actions-alarms {
    color: #fff;
  }

  .custom-popover {
    .popover-body {
      // $omnia-secondary-bg
      // $second-light
      background-color: var(--second-light) !important;
      color: #fff;
      border-color: var(--second-light) !important;
    }

    .arrow::after {
      border-left-color: var(--second-light) !important;
    }
  }
}

.custom-popover {
  margin-right: 400px;
  background-color: var(--third-dark) !important;

  .popover-body {
    color: #fff;
    max-width: 400px;
    border-color: var(--third-dark) !important;
  }

  .arrow::after {
    border-left-color: var(--third-dark) !important;
  }
}

.badge-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 100%;
  min-width: 0;
}

.badge-wrapper > .badge-title {
  flex: 0 1 auto;
  min-width: 0;
}

.badge-notification-slot {
  position: relative;
  flex: 0 0 auto;
  align-self: stretch;
  width: 15px;
  margin-right: 3px;
}

.badge-panel-device-status {
  position: relative;
  top: 1px;
  flex: 0 0 auto;
  display: inline-block;
  margin-left: auto;
  width: 15px;
  height: 15px;

  border-radius: 50%;

  &::before,
  &::after {
    content: "";
    position: absolute;
    left: 50%;
    width: 3px;
    background-color: #ffffff;
    transform: translateX(-50%);
  }

  &::before {
    top: 3px;
    height: 5px;
    border-radius: 1px;
  }

  &::after {
    top: 9px;
    height: 3px;
    border-radius: 50%;
  }
}

.badge-notification {
  position: absolute;
  top: -2px;
  right: 0;
  min-width: 17px;
  height: 15px;
  padding: 0 4px;

  background-color: #e53935;
  color: #fff;

  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;

  display: flex;
  align-items: center;
  justify-content: center;

  line-height: 1;
  pointer-events: none;
}

.alarm-acronym-column {
  min-width: 105px;
  width: 100px;
  max-width: 140px;
}

.alarm-icon-column {
  position: relative;
  width: 30px;
}

.alarm-level-column {
  width: 3px !important;
  min-width: 3px !important;
  max-width: 3px !important;
  padding: 0 !important;
  text-align: center;
  overflow: visible;
}

.level-sort-indicator {
  display: inline-block;
  font-size: 11px;
  line-height: 1;
  color: #e2e2e2;   // color por defecto (sin click)
  cursor: pointer;
  transform: translateX(-2px);
}

.level-sort-indicator.is-active {
  color: #0e1e2d;   // color cuando ya está en uso
  font-weight: 700;
}

.alarms-widget .table.b-table thead th[aria-sort] {
  background-image: none !important;
}

.alarms-widget .table.b-table thead th[aria-sort] {
  padding-right: 0.2rem !important;
}

</style>
